import { useContext, useEffect, useState } from 'react';
import { Button, Grid, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { datePickerFormat } from 'helpers';
import { EstateResultItem, EstateResultItem_PropertyReportInput } from 'queries';

import { EstateResultsPageContext } from 'pages/estate-results-page/context/estate-results-page-context';

import {
  CustomResultItemField,
  handleNewCustomFieldButtonClick,
  parseCustomFieldsJson,
  removeCustomField,
  updateCustomField,
} from '../utils/custom-result-item-field';

import NewEssentialData from './new-essential-data';

type Props = {
  estateResultItem?: EstateResultItem;
  // eslint-disable-next-line @typescript-eslint/ban-types
  setEssentialData: Function;
};

const useStyles = makeStyles(() => ({
  addressContainer: {
    paddingTop: '10px',
    textAlign: 'left',
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    gap: '5px',
  },
  button: {
    marginTop: '3px',
    padding: '2px 10px',
  },
  container: {
    paddingTop: '10px',
  },
  textField: {
    width: '100%',
  },
  inputField: {
    textAlign: 'left',
    width: '100%',
  },
  dateField: {
    paddingTop: '10px',
    textAlign: 'left',
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
  },
  datePicker: {
    padding: '5px',
  },
  title: {
    paddingTop: '10px',
  },
}));

export default function AddPropertyItem({ setEssentialData, estateResultItem }: Props) {
  const classes = useStyles();
  const context = useContext(EstateResultsPageContext);

  const [propertyInput, setPropertyInput] = useState<EstateResultItem_PropertyReportInput>({
    nameOnDeed: '',
    addressLine: '',
    cityName: '',
    stateAbbreviation: 'N/A',
    zipCode: '',
    priceOnTransaction: '',
    transactionType: '',
    parcelNumber: '',
    propertyType: '',
    transactionDate: '',
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [customFields, setCustomFields] = useState<CustomResultItemField[]>([]);
  const [selectedTransactionDate, setSelectedTransactionDate] = useState<Date>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleInputChange = (e: any) => {
    const name: string = e.target.name;

    const newInput = { ...propertyInput };
    newInput[name as keyof EstateResultItem_PropertyReportInput] = e.target.value;
    setPropertyInput(newInput);
    setEssentialData(propertyInput);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDateInputChange = (e: any, name: string) => {
    const date = new Date(e);
    const formattedDate = `${date.getMonth() + 1 < 10 ? '0' : ''}${date.getMonth() + 1}/${
      date.getDate() < 10 ? '0' : ''
    }${date.getDate()}/${date.getFullYear()}`;
    propertyInput[name as keyof EstateResultItem_PropertyReportInput] = formattedDate;
    setSelectedTransactionDate(e);
    setEssentialData(propertyInput);
  };

  const mapExistingEstateResultItemData = (estateResultItem: EstateResultItem) => {
    const essentialData = JSON.parse(estateResultItem.essentialData!);
    const newInput: EstateResultItem_PropertyReportInput = {
      nameOnDeed: essentialData.nameOnDeed || '',
      addressLine: essentialData.addressLine || '',
      cityName: essentialData.cityName || '',
      stateAbbreviation: essentialData.stateAbbreviation || 'N/A',
      zipCode: essentialData.zipCode || '',
      priceOnTransaction: essentialData.priceOnTransaction || '',
      transactionType: essentialData.transactionType || '',
      parcelNumber: essentialData.parcelNumber || '',
      propertyType: essentialData.propertyType || '',
      transactionDate: essentialData.transactionDate || '',
    };

    setSelectedTransactionDate(new Date(newInput.transactionDate) || '');
    setPropertyInput(newInput);
    setCustomFields(parseCustomFieldsJson(essentialData.customFields));
  };

  useEffect(() => {
    const essentialData: any = {};
    customFields.forEach((customField) => {
      essentialData[`customField${customField.id}`] = {
        id: customField.id,
        name: customField.field,
        value: customField.value,
      };
    });

    propertyInput.customFields = essentialData;
    setEssentialData(propertyInput);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFields, propertyInput]);

  useEffect(() => {
    if (estateResultItem) {
      mapExistingEstateResultItemData(estateResultItem);
    }
  }, [estateResultItem]);

  return (
    <>
      <Typography variant="h5" className={classes.title}>
        {context.isEditingEstateResultItem ? 'Edit Property History' : 'Add Property History'}
      </Typography>
      <Grid className={classes.container}>
        <TextField
          className={classes.inputField}
          fullWidth
          name="addressLine"
          label="Address"
          value={context.isEditingEstateResultItem ? propertyInput.addressLine : null}
          variant="outlined"
          onChange={handleInputChange}
          inputProps={{ maxLength: 50 }}
        />
      </Grid>

      <Grid className={classes.addressContainer}>
        <Grid>
          <TextField
            className={classes.inputField}
            fullWidth
            name="cityName"
            label="City"
            value={context.isEditingEstateResultItem ? propertyInput.cityName : null}
            variant="outlined"
            onChange={handleInputChange}
            inputProps={{ maxLength: 30 }}
          />
        </Grid>
        <Grid>
          <TextField
            className={classes.inputField}
            fullWidth
            name="stateAbbreviation"
            label="State"
            value={context.isEditingEstateResultItem ? propertyInput.stateAbbreviation : null}
            variant="outlined"
            onChange={handleInputChange}
            inputProps={{ maxLength: 2 }}
          />
        </Grid>
        <Grid>
          <TextField
            className={classes.inputField}
            fullWidth
            name="zipCode"
            label="Zip Code"
            value={context.isEditingEstateResultItem ? propertyInput.zipCode : null}
            variant="outlined"
            onChange={handleInputChange}
            inputProps={{ maxLength: 5 }}
          />
        </Grid>
      </Grid>
      <Grid className={classes.container}>
        <TextField
          className={classes.inputField}
          fullWidth
          name="nameOnDeed"
          label="Name on Deed"
          value={context.isEditingEstateResultItem ? propertyInput.nameOnDeed : null}
          variant="outlined"
          onChange={handleInputChange}
          inputProps={{ maxLength: 50 }}
        />
      </Grid>
      <Grid className={classes.container}>
        <TextField
          className={classes.inputField}
          fullWidth
          name="parcelNumber"
          label="Parcel Number"
          value={context.isEditingEstateResultItem ? propertyInput.parcelNumber : null}
          variant="outlined"
          onChange={handleInputChange}
          inputProps={{ maxLength: 50 }}
        />
      </Grid>
      <Grid className={classes.container}>
        <TextField
          className={classes.inputField}
          fullWidth
          name="priceOnTransaction"
          label="Price on Transaction"
          value={context.isEditingEstateResultItem ? propertyInput.priceOnTransaction : null}
          variant="outlined"
          onChange={handleInputChange}
          inputProps={{ maxLength: 50 }}
        />
      </Grid>
      <Grid className={classes.container}>
        <TextField
          className={classes.inputField}
          fullWidth
          name="propertyType"
          label="Property Type"
          value={context.isEditingEstateResultItem ? propertyInput.propertyType : null}
          variant="outlined"
          onChange={handleInputChange}
          inputProps={{ maxLength: 50 }}
        />
      </Grid>
      <Grid className={classes.container}>
        <TextField
          className={classes.inputField}
          fullWidth
          name="transactionType"
          label="Transaction Type"
          value={context.isEditingEstateResultItem ? propertyInput.transactionType : null}
          variant="outlined"
          onChange={handleInputChange}
          inputProps={{ maxLength: 50 }}
        />
      </Grid>
      <Grid className={classes.dateField}>
        <Grid className={classes.datePicker} item xs={12} sm={12}>
          <Typography variant="caption">Transaction Date</Typography>
          <DatePicker
            orientation="landscape"
            value={selectedTransactionDate}
            onChange={(e) => handleDateInputChange(e, 'transactionDate')}
            format={datePickerFormat}
            className={classes.inputField}
          />
        </Grid>
      </Grid>
      <Typography variant="h5" className={classes.title}>
        Custom Fields
      </Typography>
      <Grid className={classes.container}>
        <Grid item xs={12} sm={12}>
          {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            customFields.map((customField: CustomResultItemField, index: number) => {
              return (
                <NewEssentialData
                  key={index}
                  customField={customField}
                  updateCustomField={(updatedCustomField: CustomResultItemField) => {
                    updateCustomField(updatedCustomField, customFields, setCustomFields);
                  }}
                  handleRemoveClick={() => {
                    removeCustomField(customField, customFields, setCustomFields);
                  }}
                />
              );
            })
          }
        </Grid>
      </Grid>
      <div className={classes.container}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          className={classes.button}
          onClick={() => {
            handleNewCustomFieldButtonClick(customFields, setCustomFields);
          }}
        >
          + Add new field
        </Button>
      </div>
    </>
  );
}
